import axios from 'axios';
import { useAuth } from '../stores/authStore';
import { raiseBillingGate, isBillingGateCode } from '../stores/billingStore';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:15001',
  // FE-rel-24 — bound every request so a hung/slow backend rejects instead of
  // leaving a spinner up indefinitely. Idempotent reads that trip this are
  // retried by the response interceptor below.
  timeout: 25_000,
});

api.interceptors.request.use((cfg) => {
  const { token, activeOrgId, user } = useAuth.getState();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  // BP 03 — active-workspace scoping. Only send the header when the user has
  // switched to a workspace other than their home org; the backend validates
  // active membership and 403s `not_a_member` if it's stale (handled below).
  if (activeOrgId && activeOrgId !== user?.organization_id) {
    cfg.headers['X-Workspace-Id'] = activeOrgId;
  }
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // FE-rel-24 — transient-error retry for idempotent reads. Network errors /
    // timeouts and 502/503/504 on GET/HEAD get up to 2 backed-off retries.
    // Everything else (4xx, 401 session-expiry, 402 billing gates, 403
    // not_a_member) falls straight through to the handlers below and is NEVER
    // retried, so the P1 401 public-route skip is unaffected.
    const cfg = err.config;
    const method = (cfg?.method || 'get').toLowerCase();
    const status = err.response?.status;
    const isIdempotent = method === 'get' || method === 'head';
    const isTransient = !err.response || status === 502 || status === 503 || status === 504;
    if (cfg && isIdempotent && isTransient) {
      const attempt = ((cfg as any).__retryCount || 0) + 1;
      if (attempt <= 2) {
        (cfg as any).__retryCount = attempt;
        await new Promise((res) => setTimeout(res, 300 * attempt));
        return api(cfg);
      }
    }
    if (err.response?.status === 401) {
      // A 401 means "expired session" ONLY for authenticated requests. The
      // public-viewer endpoints deliberately return 401 for their own link
      // challenges (password_required, org_only_anonymous, org_only_wrong_org,
      // password_invalid) — treating those as a session expiry logged the
      // visitor out and hard-redirected to /login, so anonymous viewers never
      // reached the password form and a colleague's org-scoped link destroyed
      // the clicker's session. Skip logout for public routes and for requests
      // that carried no bearer token (nothing to expire); let the caller handle
      // the 401 itself.
      const url = err.config?.url || '';
      const isPublicRoute = url.includes('/api/v1/public/');
      const hadBearer = !!err.config?.headers?.Authorization;
      if (!isPublicRoute && hadBearer) {
        useAuth.getState().logout();
        if (location.pathname !== '/login') location.href = '/login';
      }
    }
    // Monetization 360 — the backend returns 402 with an `error` code for every
    // spend/feature gate (subscription_required, cost/token_limit_exceeded,
    // feature_not_in_plan). Surface it as the global upgrade modal; the
    // rejected promise still propagates so the calling code can also react.
    if (err.response?.status === 402) {
      const d = err.response.data || {};
      if (isBillingGateCode(d.error)) {
        raiseBillingGate({
          code: d.error,
          message: d.message || 'Upgrade your plan to continue.',
          feature: d.feature,
          required_plan: d.required_plan,
          used_usd: d.used_usd, cap_usd: d.cap_usd,
          used_tokens: d.used_tokens, cap_tokens: d.cap_tokens,
          subscription_status: d.subscription_status,
          // TUFG seat-gate extras.
          need: d.need, seat_type: d.seat_type, limit: d.limit, current: d.current,
        });
      }
    }
    // BP 03 — a stale active-workspace selection (membership revoked, or the
    // workspace gone) surfaces as 403 not_a_member. Reset to home + reload so
    // the user isn't stuck unable to make any request.
    if (err.response?.status === 403 && err.response?.data?.error === 'not_a_member') {
      const { activeOrgId, user, setActiveOrg } = useAuth.getState();
      if (activeOrgId) {
        setActiveOrg(null);
        // Workspace is in the URL (`/w/<id>`). A bare reload would keep the
        // stale workspace in the path and loop; navigate to the home workspace.
        const homeId = user?.organization_id;
        location.assign(homeId ? `/w/${homeId}/` : '/');
      }
    }
    return Promise.reject(err);
  },
);
